import { EventSubscriptionResponse, AggregateEventSubscriptionResponse } from "phoenix/domain";
import {
    EventHandler,
    EventRelayService
} from "phoenix/services/event-relay";
import { CommonUtilsService } from "util/util";
import { 
    IDictionary, 
    Action,
    IAsyncServiceProvider
} from "util/domain";
import { PhoenixToggleService } from "./phoenixToggleService";

const initialSubscribeRetryIntervalMs = 10 * 1000;
const maxSubscribeRetryIntervalMs = 5 * 60 * 1000;

export function eventSubscriptionServiceFactory(
    $q: ng.IQService,
    $timeout: ng.ITimeoutService,
    $http: ng.IHttpService,
    pxEventRelayService: EventRelayService,
    utils: CommonUtilsService,
    pxToggleService: PhoenixToggleService
): IAsyncServiceProvider<IEventSubscriptionService> {
    let eventSubscriptionService: IEventSubscriptionService;
    let p = pxToggleService.isEnabled('Core/EventRelayService').then(isEnabled => {
        eventSubscriptionService = isEnabled
            ? new EventSubscriptionService($q, $timeout, $http, pxEventRelayService, utils)
            : new NullEventSubscriptionService();
    });

    return {
        get instance() {
            return eventSubscriptionService;
        },
        onInit: p
    };
}

eventSubscriptionServiceFactory.$inject = [
    '$q',
    '$timeout',
    '$http',
    'pxEventRelayService',
    'utils',
    'pxPhoenixToggleService'
];

export interface IEventSubscriptionService {
    on<T>(subscribeUrl: string, eventType: string, eventHandler: EventHandler<T>): Action;
    off(): void;
}

export class EventSubscriptionService {
    private readonly unsubscribes: IDictionary<Action> = {};
    private readonly subscribeResponses: IDictionary<AggregateEventSubscriptionResponse> = {};
    /* note: this keeps track of valid, outstanding tokens as a caller may call the off() callback
    before the async subscribe operation has completed */
    private validTokens: string[] = [];

    public constructor(
        private readonly $q: ng.IQService,
        private readonly $timeout: ng.ITimeoutService,
        private readonly $http: ng.IHttpService,
        private readonly pxEventRelayService: EventRelayService,
        private readonly utils: CommonUtilsService) { }

    public on<T>(subscribeUrl: string, eventType: string, eventHandler: EventHandler<T>): Action {
        const uid = this.utils.generateUid();
        this.validTokens.push(uid);

        let retryTimeout: ng.IPromise<void> = null;
        const off = () => {
            if (retryTimeout) {
                this.$timeout.cancel(retryTimeout);
                retryTimeout = null;
            }

            if (this.unsubscribes[uid]) {
                this.unsubscribeAndRemove(uid);
            };

            this.validTokens = this.validTokens.filter(token => token !== uid);
        };

        let subscribeAndEstablishConnection = () => {
            let subscribe: ng.IPromise<AggregateEventSubscriptionResponse>;
            if (this.subscribeResponses[uid]) {
                subscribe = this.$q.resolve(this.subscribeResponses[uid]);
            }
            else {
                subscribe = this.$http
                    .post<AggregateEventSubscriptionResponse>(subscribeUrl, { "eventTypes": [ eventType ], "type": "WebSocket" })
                    .then(response => response.data);
            }

            return subscribe
                .then((aggregateEventSubscriptionResponse: AggregateEventSubscriptionResponse) => {
                    if (!this.validTokens.some(token => token === uid)) {
                        return;
                    }

                    if (aggregateEventSubscriptionResponse.FailedSubscriptionEventTypes.length) {
                        throw new Error(`Failed to subscribe to event types ${aggregateEventSubscriptionResponse.FailedSubscriptionEventTypes.join(",")}`);
                    }

                    this.subscribeResponses[uid] = aggregateEventSubscriptionResponse;

                    return this.$q.all(aggregateEventSubscriptionResponse.SuccessfulSubscriptions.map(eventSubscriptionResponse => 
                        this.pxEventRelayService.subscribe(eventSubscriptionResponse.EventName, {
                            subscriptionId: eventSubscriptionResponse.SubscriptionId,
                            joinMethod: eventSubscriptionResponse.GroupJoin,
                            eventMethod: eventSubscriptionResponse.EventReceive,
                            leaveMethod: eventSubscriptionResponse.GroupLeave
                        },
                        eventHandler)));
                })
                .then(relaySubscriptions => {
                    if (!this.validTokens.some(token => token === uid)) {
                        relaySubscriptions.forEach(s => s.unsubscribe());
                        this.subscribeResponses[uid] = null;
                        delete this.subscribeResponses[uid];
                        return;
                    }

                    let unsubscribe = () => relaySubscriptions.forEach(s => s.unsubscribe());
                    this.unsubscribes[uid] = unsubscribe;

                    this.subscribeResponses[uid] = null;
                    delete this.subscribeResponses[uid];
                    retryTimeout = null;
                })
                .then(() => {
                    this.pxEventRelayService.onConnectionLost(() => {
                        off();

                        this.validTokens.push(uid);
                        this.utils.retryWithBackoff(subscribeAndEstablishConnection,
                            initialSubscribeRetryIntervalMs,
                            maxSubscribeRetryIntervalMs,
                            (timeout) => { retryTimeout = timeout; });
                    });
                })
                .catch(err => {
                    if (err && err.status === 403) {
                        /* note: we intentionally eat this error in order to prevent further attempts to subscribe
                        to an inaccessible item */
                        console.warn("Event subscription failed: item not accessible");
                    }
                    else {
                        throw err;
                    }
                });
            };

        this.utils.retryWithBackoff(subscribeAndEstablishConnection,
            initialSubscribeRetryIntervalMs,
            maxSubscribeRetryIntervalMs,
            (timeout) => { retryTimeout = timeout; });

        return off;
    }

    private unsubscribeAndRemove(key: string) {
        this.unsubscribes[key]();
        this.unsubscribes[key] = null;
        delete this.unsubscribes[key];
    }

    public off() {
        Object.keys(this.unsubscribes).forEach(key => {
            this.unsubscribeAndRemove(key);
            this.validTokens = this.validTokens.filter(token => token !== key);
        });

        /* note: when event-relay subscriptions are more widely used throughout the product, we probably
        won't want to disconnect, but instead have a long-lived connection */
        this.pxEventRelayService.disconnect();
    }
}

class NullEventSubscriptionService implements IEventSubscriptionService {
    on<T>(subscribeUrl: string, eventType: string, eventHandler: EventHandler<T>): Action {
        return () => {};
    }

    off(): void { }
}


// WEBPACK FOOTER //
// ./src/phoenix/services/eventSubscriptionService.ts